//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  PurchaseOption,
  SamplingContentType,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { BadgeType } from '@components/atoms/badge/Badge';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { pluralize } from '@utils/stringUtils';

export interface ExpiryBadgeInfo {
  daysUntilExpiry: number;
  type: BadgeType;
}

export const getExpiryBadgeType = (
  expiryDate: Date | undefined,
  timeZone: TimeZoneId,
): ExpiryBadgeInfo | undefined => {
  if (!expiryDate) {
    return undefined;
  }
  const currentDate = DateString.getToday(timeZone);
  const expiryDateString = DateString.fromTimestamp(expiryDate, timeZone);
  const daysUntilExpiry = DateString.getDayOffset(
    currentDate,
    expiryDateString,
  );

  // Don't show badge for items expiring >90 days out
  if (daysUntilExpiry > 90) {
    return undefined;
  }

  if (daysUntilExpiry < 0) {
    return { daysUntilExpiry, type: BadgeType.Neutral };
  }
  if (daysUntilExpiry <= 30) {
    return { daysUntilExpiry, type: BadgeType.Error };
  }
  if (daysUntilExpiry <= 60) {
    return { daysUntilExpiry, type: BadgeType.Warning };
  }
  // daysUntilExpiry <= 90
  return { daysUntilExpiry, type: BadgeType.Neutral };
};

export const getExpiryBadgeText = (daysUntilExpiry: number): string => {
  if (daysUntilExpiry < 0) {
    return 'expired';
  }
  if (daysUntilExpiry === 0) {
    return 'expiring today';
  }
  return `expiring in ${pluralize(daysUntilExpiry, 'day')}`;
};

export const getContentTypesLabel = (
  purchaseOption: PurchaseOption,
): string => {
  const contentTypesParts: string[] = [];

  if (purchaseOption.contentType) {
    switch (purchaseOption.contentType) {
      case SamplingContentType.REVIEWS_ONLY:
        contentTypesParts.push('Reviews');
        break;
      case SamplingContentType.SOCIAL_ONLY:
        contentTypesParts.push('Social');
        break;
      case SamplingContentType.REVIEWS_AND_SOCIAL:
        contentTypesParts.push('Reviews', 'Social');
        break;
    }
  }

  if (purchaseOption.hasPostSurvey) {
    contentTypesParts.push('Post Survey');
  }

  if (purchaseOption.hasExternalReview) {
    contentTypesParts.push('External Review');
  }

  return contentTypesParts.join(', ');
};
